// packages/shared/src/context/UserSettingsContext.js
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import fetchUserSettings from '../data/fetchUserSettings';
import updateUserSettings from '../data/updateUserSettings';
import { useNotify } from './NotificationContext';

const UserSettingsContext = createContext(null);

export function UserSettingsProvider({ token, children }) {
  const notify = useNotify();

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!token) return null;

    setLoading(true);
    setError(null);

    try {
      const payload = await fetchUserSettings(token);
      const nextSettings = payload?.settings || payload;

      setSettings(nextSettings);
      return nextSettings;
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      refetch();
    } else {
      setSettings(null);
      setError(null);
    }
  }, [token, refetch]);

  const saveSettings = useCallback(
    async (newSettings) => {
      if (!token) return null;

      setUpdating(true);
      setError(null);

      try {
        const result = await updateUserSettings(token, newSettings);

        await refetch();

        notify.show('User settings saved');

        return result;
      } catch (e) {
        notify.show('Failed to update user settings');

        setError(e);
        throw e;
      } finally {
        setUpdating(false);
      }
    },
    [token, refetch]
  );

  useEffect(() => {
    if (token) {
      refetch();
    } else {
      setSettings(null);
    }
  }, [token, refetch]);

  return (
    <UserSettingsContext.Provider
      value={{
        settings,
        loading,
        updating,
        error,
        refetch,
        saveSettings,
      }}
    >
      {children}
    </UserSettingsContext.Provider>
  );
}

export function useUserSettingsContext() {
  const ctx = useContext(UserSettingsContext);

  if (!ctx) {
    throw new Error(
      'useUserSettingsContext must be used inside UserSettingsProvider'
    );
  }

  return ctx;
}
